import { Link, useNavigate } from 'react-router-dom'
import { ChevronLeft, FileText } from 'lucide-react'

export default function Terms() {
  const navigate = useNavigate()
  return (
    <div className="min-h-[100dvh] bg-[#0a0a12] overflow-x-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[128px] -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[128px] translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto py-8 px-4">
        <div className="backdrop-blur-xl bg-slate-900/80 border border-cyan-500/20 rounded-2xl shadow-2xl shadow-cyan-500/10">
          <div className="p-6 md:p-10 space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => navigate(-1)} className="text-cyan-400 hover:text-cyan-300 transition-colors">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <FileText className="w-6 h-6 text-cyan-400" />
              <h1 className="text-xl md:text-2xl font-bold text-white tracking-wide">TERMS OF SERVICE</h1>
            </div>
            <p className="text-slate-400 text-xs font-mono">Last updated: February 10, 2026</p>

            <div className="space-y-6 text-slate-300 text-sm leading-relaxed">
              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">1. Acceptance of Terms</h2>
                <p>
                  By creating an account or using Territory Nexus (the "Service"), you agree to be bound by
                  these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">2. Eligibility</h2>
                <p>
                  You must be at least 13 years old to use the Service. By registering, you represent that you
                  are at least 13 years of age. If you are under 18, you represent that you have your
                  parent or guardian's consent to use the Service.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">3. Account Responsibilities</h2>
                <ul className="list-disc list-inside space-y-1 text-slate-400">
                  <li>You are responsible for maintaining the security of your account credentials.</li>
                  <li>You may not share your account with others or create multiple accounts.</li>
                  <li>You are responsible for all activity that occurs under your account.</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">4. Acceptable Use</h2>
                <p>You agree not to:</p>
                <ul className="list-disc list-inside space-y-1 text-slate-400">
                  <li>Use GPS spoofing, location mocking, or any method to falsify your physical location.</li>
                  <li>Use bots, scripts, automation tools, or modified clients to interact with the Service.</li>
                  <li>Exploit bugs, glitches, or vulnerabilities instead of reporting them.</li>
                  <li>Create multiple accounts to gain unfair advantages.</li>
                  <li>Harass, threaten, or abuse other players.</li>
                  <li>Attempt to access other players' accounts or private data.</li>
                  <li>Reverse-engineer, decompile, or tamper with the Service.</li>
                  <li>Use the Service for any illegal purpose.</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">5. Location-Based Gameplay</h2>
                <p>
                  Territory Nexus is a location-based game that requires access to your device's GPS.
                  By using the Service, you consent to the collection and use of your location data
                  as described in our <Link to="/privacy" className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2">Privacy Policy</Link>.
                </p>
                <p>
                  You are responsible for your physical safety while playing. Do not use the Service
                  while driving, in restricted areas, or in any situation where it could endanger
                  yourself or others. We are not liable for any injuries, accidents, or incidents
                  that occur while playing.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">6. Virtual Items and Currency</h2>
                <p>
                  All in-game items, units, resources, zones, and other virtual goods are owned by us
                  and licensed to you for personal, non-commercial use. Virtual items have no real-world
                  monetary value and cannot be exchanged, sold, or transferred outside the Service.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">7. Enforcement and Termination</h2>
                <p>
                  We reserve the right to suspend or terminate your account at any time, with or without
                  notice, for violations of these Terms. This includes but is not limited to: cheating,
                  GPS spoofing, multi-accounting, exploiting bugs, or abusive behavior.
                </p>
                <p>
                  Upon termination, your right to access the Service ceases immediately. We may
                  delete your account data in accordance with our Privacy Policy.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">8. Service Availability</h2>
                <p>
                  The Service is provided "as is" and "as available". We do not guarantee uninterrupted
                  access. We may modify, suspend, or discontinue the Service at any time without notice.
                  We are not liable for any loss of game progress, virtual items, or data resulting from
                  service interruptions.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">9. Limitation of Liability</h2>
                <p>
                  To the maximum extent permitted by law, Territory Nexus and its operators shall not be
                  liable for any indirect, incidental, special, consequential, or punitive damages arising
                  from your use of the Service, including but not limited to loss of data, personal injury,
                  or property damage.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">10. Changes to Terms</h2>
                <p>
                  We may update these Terms from time to time. We will notify you of material changes
                  by posting the updated Terms in the app. Continued use of the Service after changes
                  constitutes acceptance of the updated Terms.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">11. Contact</h2>
                <p>
                  If you have questions about these Terms, please contact us at{' '}
                  <span className="text-cyan-400">support@brokenzones.com</span>.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
